import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { RecordRow } from '../lib/api';
import { listAllRecordsByType } from '../lib/api';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './Registry.css';

// A3 — ONE LIST SURFACE, RENDERED FROM THE PACK'S OWN TYPES.
//
// Auditions, Projects, People and the ledger/craft groups are this one
// account's own — nine destinations for the tenant's own records, and the
// declaration is written once, here, so each tab is a thin declaration of
// which types it shows.
//
// WHY THIS IS NOT A PLACEHOLDER. A tab in a declared rail that goes nowhere
// is worse than a tab that does not exist: the pack promises the parent nine
// destinations and the shell must keep the promise. Every row here is a real
// record, read through the ordinary gated door, and every row opens the
// schema-driven editor — the same door CareerMoney's ledger rows have always
// used. The surface is thin; it is not fake.
//
// WHAT IT IS NOT YET, said plainly so nobody mistakes it for finished: the
// editorial audition spread, the Project › Role › Audition hierarchy and the
// sides revision trail are designed work (the five blessed decisions) and are
// their own tranche. When they land they replace the Auditions and Projects
// declarations below; nothing else about this file changes.
//
// ⚑ 2026-09-19 — the subject scope this file used to read through
// (lib/subjectScope.ts) is gone. An account is one career (Huan's ruling),
// so there is no performer to narrow into and no household to narrow from:
// every type here is read tenant-wide, full stop.

export interface RegistrySection {
  /** The pack's own type key — aa_document, aa_training, … */
  typeKey: string;
  /** What the section is called. Plural, the pack's own label. */
  label: string;
  /** What the section says when it holds nothing. A statement of fact, never
   * an instruction: the shell's persistent "+" is how anything is created
   * here, and a list that explains the button is a list admitting the button
   * does not teach itself. */
  empty: string;
}

export interface RegistryProps {
  tenantId: string;
  /** The mono line above the screen — the tab's own name. */
  eyebrow: string;
  sections: RegistrySection[];
  /** ⚑ W6: something that belongs ABOVE the lists on this tab, rendered
   * inside this screen's own scrolling root.
   *
   * The Development tab's craft panel is the first of these. It could not
   * simply be wrapped around <Registry> by the caller: `.app-shell` is
   * `overflow: hidden` and every screen root scrolls itself, so a wrapper div
   * would have clipped both at the fold — invisibly, since jsdom computes no
   * layout (ScreenReachability.test.ts's second half exists for exactly this
   * failure). Passing it IN keeps one scrolling root.
   *
   * Rendered in the loading and refused states too, so a panel that has its
   * own data does not blink out while the registry's read is in flight. */
  lead?: ComponentChildren;
}

type LoadState = 'loading' | 'ready' | 'error';

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export function Registry({ tenantId, eyebrow, sections, lead }: RegistryProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [rowsByType, setRowsByType] = useState<Record<string, RecordRow[]>>({});
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  // The section list is rebuilt on every render by its caller, so the effect
  // keys off the type keys rather than the array identity — otherwise this
  // read would re-run forever.
  const typeKeys = sections.map((s) => s.typeKey).join(',');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setState('loading');
      setFailure(null);
      try {
        const keys = typeKeys.split(',').filter(Boolean);
        const results = await Promise.all(
          // Paged to the whole truth. A section headed "Documents 50" on a
          // household with 60 is not a shorter answer, it is a wrong one.
          keys.map((typeKey) => listAllRecordsByType(tenantId, typeKey))
        );
        if (cancelled) return;
        const next: Record<string, RecordRow[]> = {};
        keys.forEach((typeKey, i) => {
          // A retired record is not part of a registry. Same rule the Rexx
          // lists hold: core's listRecords excludes only status 'deleted',
          // so an archived row would otherwise still be counted and shown.
          next[typeKey] = activeOnly(results[i] ?? []);
        });
        setRowsByType(next);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setState('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, typeKeys, retryNonce, recordsVersion]);

  if (state === 'loading') {
    return (
      <div class="registry">
        <div class="registry__eyebrow">{eyebrow}</div>
        {lead}
        <div class="registry__say">One moment…</div>
      </div>
    );
  }

  if (state === 'error' && failure !== null) {
    // A refused read is never rendered as an empty registry. Which refusal
    // it was, and the retry when there is one.
    return (
      <div class="registry">
        <div class="registry__eyebrow">{eyebrow}</div>
        {lead}
        <div class="registry__say">
          {failure.headline}
          <br />
          <span class="registry__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="registry__more" onClick={() => setRetryNonce((n) => n + 1)}>
            Read this again
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div class="registry">
      <div class="registry__eyebrow">{eyebrow}</div>
      {lead}
      {sections.map((section) => {
        const rows = rowsByType[section.typeKey] ?? [];
        return (
          <div key={section.typeKey}>
            <div class="registry__sec">
              <span>{section.label}</span>
              {rows.length > 0 ? <span class="registry__mini">{rows.length}</span> : null}
            </div>
            {rows.length === 0 ? (
              <div class="registry__empty">{section.empty}</div>
            ) : (
              rows.map((row) => (
                <button
                  type="button"
                  class="registry__row"
                  key={row.id}
                  onClick={() =>
                    openRecordEditor({ mode: 'edit', typeKey: section.typeKey, id: row.id })
                  }
                >
                  <span class="registry__row-name">{row.display_name}</span>
                  {/* The lifecycle's own word, never a re-labelling of it.
                      A record with no lifecycle says nothing rather than
                      inventing a state for it. */}
                  {row.lifecycle_state ? (
                    <span class="registry__row-state">{humanize(row.lifecycle_state)}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}
